import { useEffect, useMemo, useState } from 'react';

import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { OptionProps } from '@amelio/ui-library';

import {
    CycleEvaluationStatus,
    EvaluationType,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import {
    appendOptionUnlessValuePresent,
    CYCLE_LIST_PAGE_SIZE,
    getGroupEvaluationTypes,
    getTypeBadgeLabelKey,
    paginateCycleListRows,
    selectGroupEvaluationTemplates,
    selectLegacyCycleEvaluations,
} from '@/pages/CycleEvaluation/Dashboard/cycleListRows';
import {
    getGroupStatusLabelKey,
    getLegacyStatusLabelKey,
} from '@/pages/CycleEvaluation/Dashboard/cycleStatus';
import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import { lastPageOf } from '@/utils/pagination';

interface UseDashboardFiltersParams {
    cycleEvaluationList: CycleEvaluation[] | undefined;
    cycleEvaluationGroupList: CycleEvaluationGroup[] | undefined;
}

export const useDashboardFilters = ({
    cycleEvaluationList,
    cycleEvaluationGroupList,
}: UseDashboardFiltersParams) => {
    const { t } = useAmelioTranslations();

    const [searchText, setSearchText] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);

    const statusFilterOptions: OptionProps[] = useMemo(() => {
        const groupOptions = Object.values(CycleEvaluationGroupStatus).map((status) => {
            const key = getGroupStatusLabelKey(status);

            return { value: key, label: t(key) };
        });

        const legacyOnlyKey = getLegacyStatusLabelKey(CycleEvaluationStatus.INACTIVE);

        return appendOptionUnlessValuePresent(groupOptions, {
            value: legacyOnlyKey,
            label: t(legacyOnlyKey),
        });
    }, [t]);

    const typeFilterOptions: OptionProps[] = useMemo(
        () =>
            Object.values(EvaluationType).map((type) => ({
                value: type,
                label: t(getTypeBadgeLabelKey(type)),
            })),
        [t],
    );

    const onToggleStatusFilter = (value: string) => {
        setStatusFilter((current) =>
            current.includes(value)
                ? current.filter((entry) => entry !== value)
                : [...current, value],
        );
    };

    const onToggleTypeFilter = (value: string) => {
        setTypeFilter((current) =>
            current.includes(value)
                ? current.filter((entry) => entry !== value)
                : [...current, value],
        );
    };

    const filteredCycleEvaluationGroups = useMemo(() => {
        const groups = cycleEvaluationGroupList ?? [];

        // `name` is typed as required, but the service casts the server response with no runtime
        // validation, so an incomplete group record must not crash the search.
        // Search matches on both names: the cycle name that titles the row and the template names
        // that qualify it.
        const searchLower = searchText.toLowerCase();
        const searchMatching = !searchText
            ? groups
            : groups.filter(
                  (group) =>
                      (group.name ?? '').toLowerCase().includes(searchLower) ||
                      selectGroupEvaluationTemplates({
                          group,
                          cycleEvaluations: cycleEvaluationList ?? [],
                      }).some(({ title }) =>
                          Object.values(title ?? {}).some((value) =>
                              value.toLowerCase().includes(searchLower),
                          ),
                      ),
              );

        const statusMatching =
            statusFilter.length === 0
                ? searchMatching
                : searchMatching.filter((group) =>
                      statusFilter.includes(getGroupStatusLabelKey(group.status)),
                  );

        const typeMatching =
            typeFilter.length === 0
                ? statusMatching
                : statusMatching.filter((group) =>
                      getGroupEvaluationTypes(group).some((type) => typeFilter.includes(type)),
                  );

        // Ordered by evaluation period only, never by status: deactivating or completing a cycle
        // must leave it where the reader last saw it rather than send it to the end (#16306).
        return [...typeMatching].sort((a, b) => {
            if (!a.evaluationPeriodStart) {
                return b.evaluationPeriodStart ? 1 : 0;
            }

            if (!b.evaluationPeriodStart) {
                return -1;
            }

            return (
                new Date(b.evaluationPeriodStart).getTime() -
                new Date(a.evaluationPeriodStart).getTime()
            );
        });
    }, [cycleEvaluationGroupList, cycleEvaluationList, searchText, statusFilter, typeFilter]);

    const filteredCycleEvaluations = useMemo(() => {
        const list = selectLegacyCycleEvaluations(
            cycleEvaluationList ?? [],
            cycleEvaluationGroupList ?? [],
        );

        const searchMatching = !searchText
            ? list
            : list.filter((evaluation) => {
                  const searchLower = searchText.toLowerCase();
                  const reviewTemplateTitle = evaluation?.cycleQuestionnaire?.title;
                  const nameMatch = reviewTemplateTitle
                      ? Object.values(reviewTemplateTitle).some((value) =>
                            value.toLowerCase().includes(searchLower),
                        )
                      : (evaluation.title?.toLowerCase().includes(searchLower) ?? false);

                  // `reviewType` is typed as required, but the service casts the server response
                  // with no runtime validation, so both reads below stay guarded against a record
                  // that omits it.
                  const typeLabel = evaluation.reviewType
                      ? t(getTypeBadgeLabelKey(evaluation.reviewType))
                      : undefined;

                  const typeMatch =
                      (typeLabel?.toLowerCase().includes(searchLower) ?? false) ||
                      (evaluation.reviewType?.toLowerCase().includes(searchLower) ?? false);

                  return nameMatch || typeMatch;
              });

        const statusMatching =
            statusFilter.length === 0
                ? searchMatching
                : searchMatching.filter((evaluation) =>
                      statusFilter.includes(getLegacyStatusLabelKey(evaluation.status)),
                  );

        return typeFilter.length === 0
            ? statusMatching
            : statusMatching.filter((evaluation) => typeFilter.includes(evaluation.reviewType));
    }, [cycleEvaluationList, cycleEvaluationGroupList, searchText, statusFilter, typeFilter, t]);

    const totalCycleCount = filteredCycleEvaluationGroups.length + filteredCycleEvaluations.length;
    const totalPages = lastPageOf(totalCycleCount, CYCLE_LIST_PAGE_SIZE);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, statusFilter, typeFilter]);

    useEffect(() => {
        setCurrentPage((page) => Math.min(page, totalPages));
    }, [totalPages]);

    const { groupsPage: cycleEvaluationGroups, evaluationsPage: cycleEvaluations } = useMemo(
        () =>
            paginateCycleListRows({
                groups: filteredCycleEvaluationGroups,
                evaluations: filteredCycleEvaluations,
                currentPage,
                pageSize: CYCLE_LIST_PAGE_SIZE,
            }),
        [filteredCycleEvaluationGroups, filteredCycleEvaluations, currentPage],
    );

    return {
        searchText,
        setSearchText,
        statusFilter,
        statusFilterOptions,
        onToggleStatusFilter,
        typeFilter,
        typeFilterOptions,
        onToggleTypeFilter,
        cycleEvaluationGroups,
        cycleEvaluations,
        currentPage,
        onCyclePageChange: setCurrentPage,
        totalCycleCount,
        cyclePageSize: CYCLE_LIST_PAGE_SIZE,
    };
};
